function SVGIcon({ name, className = 'w-5 h-5' }) {
  const icons = {
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    leaf: <path d="M11 20A9 9 0 0 1 2 11c0-4.97 4.03-9 9-9 4.97 0 9 4.03 9 9 0 4.97-4.03 9-9 9zm0-16a7 7 0 0 0-7 7c0 3.87 3.13 7 7 7 3.87 0 7-3.13 7-7 0-3.87-3.13-7-7-7zm-1 9V8h2v5h-2zm0 4v-2h2v2h-2z" />,
    zap: <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />,
    check: <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3" />,
    award: <path d="M12 15l-2 5 3-1.5L16 20l-2-5 4-4h-5.5L12 5.5 10.5 11H5l4 4z" />,
    mapPin: <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0zM12 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />,
  };
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      {icons[name] || icons.shield}
    </svg>
  );
}

export default function About() {
  return (
    <main className="bg-bg overflow-hidden">
      {/* Hero */}
      <section className="relative min-h-[320px] sm:min-h-[380px] md:min-h-[440px] flex items-center px-3 min-[480px]:px-5 sm:px-6 md:px-16 py-12 sm:py-16 md:py-20 overflow-hidden bg-gradient-to-b from-primary-dark via-primary to-primary-deep text-white">
        <div className="absolute inset-0 opacity-15 bg-[radial-gradient(var(--color-accent-bright)_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none" />
        <div className="absolute right-[10%] top-[10%] w-48 sm:w-72 h-48 sm:h-72 rounded-full bg-accent-bright/15 blur-3xl pointer-events-none float-slow" />
        <div className="absolute left-[5%] bottom-[5%] w-40 sm:w-60 h-40 sm:h-60 rounded-full bg-cta/20 blur-3xl pointer-events-none float-medium" />

        <div className="relative z-10 max-w-3xl mx-auto text-center animate-rise">
          <span className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 sm:py-2 rounded-full bg-accent/20 text-accent-bright text-[9px] min-[480px]:text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-3 sm:mb-5 border border-accent/30 shadow-sm backdrop-blur-md">
            <span className="w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 rounded-full bg-accent-bright animate-ping shrink-0" />
            ISO 9001:2015 &amp; FoSTaC QUALIFIED
          </span>
          <h1 className="font-heading text-2xl min-[480px]:text-3xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-3 sm:mb-5 tracking-tight px-2 sm:px-0">
            Prevention is our <span className="text-accent-bright">promise.</span>
          </h1>
          <p className="text-white/80 text-[11px] min-[480px]:text-xs sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto font-normal px-2 sm:px-0">
            Certified pest management engineered for homes, commercial complexes, and food manufacturing units where safety and compliance matter.
          </p>
        </div>
      </section>

      {/* Curved Divider Top (Hero -> Company Section) */}
      <div className="w-full overflow-hidden leading-none bg-primary-dark -mt-1 -mb-1 relative z-10 pointer-events-none">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-14 text-accent-bright fill-current">
          <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,60 L1200,120 L0,120 Z" />
        </svg>
      </div>

      {/* Company Section */}
      <section className="py-10 sm:py-14 md:py-20 px-3 min-[480px]:px-5 sm:px-6 md:px-16 bg-surface-alt relative">
        <div className="max-w-5xl mx-auto rounded-2xl min-[480px]:rounded-3xl overflow-hidden shadow-xl grid grid-cols-1 lg:grid-cols-2 bg-white border border-slate-200">
          {/* Left Column: Responsive AI Pest Control Image Container */}
          <div className="relative overflow-hidden min-h-[220px] min-[480px]:min-h-[280px] sm:min-h-[380px] lg:min-h-[100%] group">
            <img
              src="/assets/about_pest_control.png"
              alt="Preventive Pest Terminators AI Smart Scanner Technician"
              className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 absolute inset-0"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 via-primary-dark/20 to-transparent pointer-events-none" />
            
            {/* Floating Quality Assurance Badge */}
            <div className="absolute bottom-3 left-3 right-3 sm:bottom-6 sm:left-6 sm:right-6 p-2.5 sm:p-4 rounded-xl sm:rounded-2xl glass-dark border border-white/20 backdrop-blur-md text-white flex items-center justify-between shadow-xl">
              <div>
                <span className="text-[8px] sm:text-[10px] font-bold tracking-widest text-accent-bright uppercase block">AI SMART SCANNING</span>
                <span className="text-[10px] sm:text-xs font-semibold text-white">Precision Nest &amp; Termite Detection</span>
              </div>
              <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl bg-accent-bright/20 border border-accent-bright/40 text-accent-bright grid place-items-center font-bold text-[10px] sm:text-sm shrink-0">
                ✦
              </span>
            </div>
          </div>
          <div className="p-4 min-[480px]:p-6 sm:p-8 md:p-14 flex flex-col justify-center">
            <span className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full bg-accent-soft text-accent-muted text-[9px] min-[480px]:text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-3 self-start border border-accent/20 shadow-2xs">
              OUR COMPANY
            </span>
            <h2 className="font-heading text-xl min-[480px]:text-2xl sm:text-3xl md:text-4xl font-bold text-primary leading-tight mb-2.5 sm:mb-3.5 tracking-tight">
              Trusted expertise. <span className="text-cta">Responsible care.</span>
            </h2>
            <p className="text-[11px] min-[480px]:text-xs sm:text-sm text-txt-muted leading-relaxed max-w-lg mb-4 sm:mb-6 font-medium">
              Preventive Pest Terminators delivers professional pest-control programmes for residential, commercial and regulated industrial premises. Our team is fully qualified for AIB, ISO, USFDA, FDA, HACCP, HALAL and FSSAI audits.
            </p>

            <div className="grid grid-cols-3 gap-1.5 min-[480px]:gap-2.5 sm:gap-3">
              {[
                ['ISO 9001', 'Certified Company', 'award'],
                ['FoSTaC', 'Certified Team', 'shield'],
                ['8 Branches', 'Across Maharashtra', 'mapPin'],
              ].map(([stat, label, icon]) => (
                <div key={stat} className="p-2 min-[480px]:p-2.5 sm:p-3.5 bg-surface-alt rounded-xl sm:rounded-2xl border border-slate-200 text-center">
                  <SVGIcon name={icon} className="w-3.5 h-3.5 min-[480px]:w-4 min-[480px]:h-4 sm:w-5 sm:h-5 text-cta mx-auto mb-0.5 sm:mb-1" />
                  <b className="font-heading text-[10px] min-[480px]:text-xs sm:text-base font-bold text-primary block leading-tight">{stat}</b>
                  <span className="text-[7px] min-[480px]:text-[8px] sm:text-[9px] uppercase tracking-wider text-txt-muted font-bold block mt-0.5 leading-tight">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Equipment */}
      <section className="py-10 sm:py-14 md:py-20 px-3 min-[480px]:px-5 sm:px-6 md:px-16 bg-bg text-center">
        <div className="max-w-5xl mx-auto">
          <span className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full bg-accent-soft text-accent-muted text-[9px] min-[480px]:text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-2.5 sm:mb-4 border border-accent/20 shadow-2xs">
            EQUIPMENT &amp; SPECIFICATIONS
          </span>
          <h2 className="font-heading text-xl min-[480px]:text-2xl sm:text-4xl md:text-5xl font-bold text-primary tracking-tight mb-8 sm:mb-14">
            Equipped for <span className="text-cta">better outcomes.</span>
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 min-[480px]:gap-3 sm:gap-6">
            {[
              ['Fly Catcher Machine', 'Professional UV fly-control equipment.', 'chart'],
              ['Rodent Bait Boxes', 'Tamper-proof bait station solutions.', 'zap'],
              ['Odorless Gel Spray', 'Kitchen & prep zone safe formulation.', 'shield'],
              ['Glue Traps & Netting', 'Humane bird netting & trap setup.', 'document'],
            ].map(([item, desc, iconName]) => (
              <article key={item} className="card-3d-hologram p-3 min-[480px]:p-4 sm:p-6 text-left group flex flex-col justify-between bg-white rounded-2xl sm:rounded-3xl border border-slate-200 shadow-md">
                <div>
                  <div className="w-8 h-8 min-[480px]:w-9 min-[480px]:h-9 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-accent-soft border border-accent/40 text-cta flex items-center justify-center mb-2.5 sm:mb-5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <SVGIcon name={iconName} className="w-4 h-4 min-[480px]:w-4.5 min-[480px]:h-4.5 sm:w-6 sm:h-6 text-cta" />
                  </div>
                  <h3 className="font-heading text-[11px] min-[480px]:text-xs sm:text-base font-bold text-primary mb-1 sm:mb-1.5 group-hover:text-cta transition-colors leading-tight">{item}</h3>
                  <p className="text-[9px] min-[480px]:text-[10px] sm:text-xs text-txt-muted leading-relaxed mb-2.5 sm:mb-4 font-medium">{desc}</p>
                </div>
                <div className="pt-2 sm:pt-3 border-t border-slate-100 flex items-center gap-1 sm:gap-1.5 text-[8px] min-[480px]:text-[9px] sm:text-[11px] font-bold text-accent-muted">
                  <span className="text-accent-bright">✦</span> Standard Specification
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
